import {
  Directive,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { DataType } from "@app/models/search/data-type.enum";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { GridComponent } from "@app/view/shared/base-pages/base-data-table-page/grid.component";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";
import {
  ColumnConfig,
  ColumnStore,
  DateColumnConfig,
  GridRowModel,
  NumberColumnConfig,
  WidgetColumnConfig
} from "@bryntum/grid-thin";
import { RealTimeDataStore } from "@app/services/store/real-time-data-store";
import { TicketDialogColumn } from "@app/view/shared/base-pages/base-data-table-page/ticket-dialog.column";
import { JobJumpColumn } from "@app/view/shared/base-pages/base-data-table-page/job-jump.column";
import { VisitBookColumn } from "@app/view/shared/base-pages/base-data-table-page/visit-book.column";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Resource } from "@lagoshny/ngx-hateoas-client";
import { Collection, CollectionFilter } from "@bryntum/core-thin";
import { MeetingJumpColumn } from "@app/view/shared/base-pages/base-data-table-page/meeting-jump.column";

@Directive()
export abstract class BaseDataTablePageDirective<
  T extends Resource
> implements OnInit {
  public DataType = DataType;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(GridComponent, { static: true }) grid: GridComponent;

  public length = 0;
  public pageSize = 10;
  public pageSizeOptions = [10, 25, 100, 1000, 10000];
  public pageEvent: any;
  @Output() gridChanged = new EventEmitter<boolean>();

  @Input()
  public columns: (
    ColumnConfig | DateColumnConfig | WidgetColumnConfig | NumberColumnConfig
  )[];
  public gridStore: CustomAjaxStore<T>;
  fixedSetOperators = {
    string: [
      {
        value: "isIncludedIn",
        text: "Filter by values",
        argCount: 1
      }
    ]
  };
  stringOperators = {
    string: [
      {
        value: "*",
        text: "contains",
        argCount: 1
      },
      {
        value: "=",
        text: "equals",
        argCount: 1
      },
      {
        value: "!=",
        text: "not equals",
        argCount: 1
      }
    ]
  };
  dateOperators = {
    date: [
      {
        value: "=",
        text: "Equals",
        argCount: 1
      },
      {
        value: ">",
        text: "After",
        argCount: 1
      },
      {
        value: "<",
        text: "Before",
        argCount: 1
      },
      {
        value: "between",
        text: "Between",
        argCount: 2
      },
      {
        value: "isToday",
        text: "Today",
        argCount: 0
      },
      {
        value: "isTomorrow",
        text: "Tomorrow",
        argCount: 0
      },
      {
        value: "isYesterday",
        text: "Yesterday",
        argCount: 0
      },
      {
        value: "isThisWeek",
        text: "This Week",
        argCount: 0
      },
      {
        value: "isLastWeek",
        text: "Last Week",
        argCount: 0
      },
      {
        value: "isNextWeek",
        text: "Next Week",
        argCount: 0
      },
      {
        value: "isThisMonth",
        text: "This Month",
        argCount: 0
      },
      {
        value: "isLastMonth",
        text: "Last Month",
        argCount: 0
      },
      {
        value: "isNextMonth",
        text: "Next Month",
        argCount: 0
      },
      {
        value: "isThisYear",
        text: "This Year",
        argCount: 0
      },
      {
        value: "isLastYear",
        text: "Last Year",
        argCount: 0
      },
      {
        value: "isNextYear",
        text: "Next Year",
        argCount: 0
      }
    ]
  };

  /* eslint-disable @angular-eslint/prefer-inject -- Abstract base class requires constructor params for super() calls from subclasses */
  protected constructor(
    protected systemSearchMetaRestService: SystemSearchMetaRestService,
    protected dataStore: RealTimeDataStore<T>,
    protected store: Store<any>,
    protected dialog: MatDialog,
    protected entityName: string,
    protected formBuilder: UntypedFormBuilder,
    public serverErrorHandler: ServiceErrorHandlerService,
    protected ngxUiLoaderService: NgxUiLoaderService
  ) {
    /* eslint-enable @angular-eslint/prefer-inject */
    this.pageEvent = new PageEvent();
    this.pageEvent.pageIndex = 0;
    this.pageEvent.pageSize = this.pageSize;
  }

  selectPage() {
    this.gridStore.pageSize = this.pageEvent.pageSize;
    this.gridStore.currentPage = this.pageEvent.pageIndex + 1;
    this.gridStore.load({}).then(() => {});
  }

  ngOnInit() {
    ColumnStore.registerColumnType(JobJumpColumn as any, true);
    ColumnStore.registerColumnType(MeetingJumpColumn as any, true);
    ColumnStore.registerColumnType(TicketDialogColumn as any, true);
    ColumnStore.registerColumnType(VisitBookColumn as any, true);

    this.gridStore = new CustomAjaxStore(
      {
        modelClass: GridRowModel,
        pageSize: "25",
        pageParamName: "page",
        filterParamName: "anything",
        sortParamName: "sort",
        responseTotalProperty: "page.totalElements",
        responseDataProperty: "_embedded.jobs",
        autoLoad: false
      },
      this.dataStore
    );

    this.gridStore.on("filter", () => {
      console.log("GRID FILTERED");
      this.gridChanged.emit(true);
    });
    this.gridStore.on("sort", () => {
      console.log("GRID SORTED");
      this.gridStore.performSort(true);
      this.gridChanged.emit(true);
    });
  }

  public persistState(): string {
    let filters = this.grid.gridEngine.store.filters;
    console.log("filters", filters);
    if (filters instanceof Collection) {
      let filterCollection = filters as Collection;
      let badFilters = [];
      (filterCollection as any).forEach((filter) => {
        if (!(filter instanceof CollectionFilter)) {
          badFilters.push(filter);
        }
      });
      filterCollection.remove(badFilters);
    }
    return JSON.stringify(this.grid.gridEngine.getState());
  }

  public restoreState(state: string) {
    console.log("Restoring state", this.grid);
    try {
      this.grid.gridEngine.applyState(JSON.parse(state));

      // After applying saved state, update sortable from current column definitions
      // This ensures saved views from older versions get the latest sortable settings
      this.applySortableFromColumnDefinitions();
    } catch (error) {}
  }

  /**
   * Updates the sortable property of grid columns from the current column definitions.
   * This ensures that saved views from older versions inherit the latest sortable
   * settings, allowing users to sort columns that were previously disabled.
   */
  private applySortableFromColumnDefinitions(): void {
    if (!(this.grid.gridEngine.columns instanceof ColumnStore)) {
      return;
    }
    const columnStore = this.grid.gridEngine.columns as ColumnStore;

    // Build maps for matching columns by field and by text (header)
    const sortableByField = new Map<string, boolean>();
    const sortableByText = new Map<string, boolean>();

    for (const col of this.columns) {
      // Default to true (sortable) unless explicitly set to false
      const sortable = col.sortable !== false;

      if (col.field) {
        sortableByField.set(col.field, sortable);
      }
      if (col.text) {
        sortableByText.set(col.text, sortable);
      }
    }

    // Update each column in the grid with the current sortable setting
    columnStore.forEach((column: any) => {
      // Try to match by field first, then by text (header name)
      if (column.field && sortableByField.has(column.field)) {
        column.sortable = sortableByField.get(column.field);
      } else if (column.text && sortableByText.has(column.text)) {
        column.sortable = sortableByText.get(column.text);
      } else if (column.field) {
        // For any unmatched column with a field, default to sortable=true
        // since deep sort now supports nested fields
        column.sortable = true;
      }
    });
  }
  public restoreStateLegacy(state: string) {
    const stateObj = JSON.parse(state);
    //
    if (this.grid.gridEngine.columns instanceof ColumnStore) {
      const columnStore: ColumnStore = this.grid.gridEngine.columns;
      columnStore.removeAll();
      columnStore.add(
        stateObj.columns.filter((column) => {
          return !column.id || column.id.indexOf("expander") === -1;
        })
      );
    }

    console.log("Resetting filters", stateObj.filters);
    if (stateObj.filters) {
      this.grid.store.filters.clear();
      stateObj.filters.forEach((filter) => {
        filter.config = {};
      });
      this.grid.store.filters.add(stateObj.filters);
    }

    // After applying legacy state, update sortable from current column definitions
    this.applySortableFromColumnDefinitions();
  }

  undo() {
    console.log(this.gridStore.changes);
    // @ts-ignore
    this.gridStore.load({});
  }

  commit() {
    console.log("Beginning commit");
    let commit: Promise<any> = this.gridStore.commit() as Promise<any>;
    commit
      .then((commitState) => {
        console.log("Commit complete", commitState);

        this.gridStore.load({}).then();
        console.log("End commit");
      })
      .catch((reason) => {
        console.log("Commit caught", reason);
        this.serverErrorHandler.handleError(reason);
        this.gridStore.load({}).then();
      });
  }

  openJob(record: any) {
    console.log("firing default open job");
  }

  openMeeting(record: any) {
    console.log("firing default open meeting");
  }

  ticketDialog(record: any) {
    console.log("firing default ticket dialog");
  }

  visitBook(record: any) {
    console.log("firing default visit dialog");
  }

  onEvent($event: any) {
    if ($event.type === "columnchange") {
      console.log("emitting toggle change");
      this.gridChanged.emit(true);
    }

    if ($event.type === "jobjump") {
      console.log($event);
      this.openJob($event.record);
    }
    if ($event.type === "meetingjump") {
      console.log($event);
      this.openMeeting($event.record);
    }
    if ($event.type === "ticketdialog") {
      this.ticketDialog($event.record);
    }
    if ($event.type === "visitbook") {
      this.visitBook($event.record);
    }

    if (
      $event.type === "mouseover" ||
      $event.type === "mouseout" ||
      $event.type === "cellmouseover" ||
      $event.type === "cellmouseout" ||
      $event.type === "cellmouseenter" ||
      $event.type === "cellmouseleave" ||
      $event.type === "rowmouseenter" ||
      $event.type === "rowmouseleave" ||
      $event.type === "renderrow" ||
      $event.type === "beforerenderrow"
    ) {
    } else {
      console.log($event.type);
    }
  }
}
